import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/api';
import type { AccessMatrixQuery, ProductRole } from '@/api/products';
import type { CreateProductFormData } from '@/types';

export function useProducts(organizationId?: string) {
  return useQuery({
    queryKey: ['products', { organizationId }],
    queryFn: () => productsApi.list(organizationId),
  });
}

export function useProductsPage(query: {
  organizationId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
}) {
  return useQuery({
    queryKey: ['products', 'page', query],
    queryFn: () => productsApi.listPage(query),
    placeholderData: (previous) => previous,
  });
}

export function useAccessMatrix(query: AccessMatrixQuery) {
  return useQuery({
    queryKey: ['access-matrix', query],
    queryFn: () => productsApi.getAccessMatrix(query),
    placeholderData: (previous) => previous,
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => productsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductFormData) => productsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateProductFormData> }) =>
      productsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products', id] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useProductAccess(productId: string | undefined) {
  return useQuery({
    queryKey: ['products', productId, 'access'],
    queryFn: () => productsApi.listAccess(productId!),
    enabled: !!productId,
  });
}

export function useGrantProductAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      data: { user_id: string; roles?: string[]; permissions?: string[]; expires_at?: string };
    }) => productsApi.grantAccess(productId, data),
    onSuccess: (_, { productId, data }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'access'] });
      queryClient.invalidateQueries({ queryKey: ['users', data.user_id, 'products'] });
      queryClient.invalidateQueries({ queryKey: ['access-matrix'] });
    },
  });
}

export function useRevokeProductAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, userId }: { productId: string; userId: string }) =>
      productsApi.revokeAccess(productId, userId),
    onSuccess: (_, { productId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'access'] });
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'products'] });
      queryClient.invalidateQueries({ queryKey: ['access-matrix'] });
    },
  });
}

export function useProductRoles(productId: string | undefined) {
  return useQuery({
    queryKey: ['products', productId, 'roles'],
    queryFn: () => productsApi.listRoles(productId!),
    enabled: !!productId,
  });
}

export function useCreateProductRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: ProductRole }) =>
      productsApi.createRole(productId, data),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'roles'] });
    },
  });
}

export function useDeleteProductRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, name }: { productId: string; name: string }) =>
      productsApi.deleteRole(productId, name),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'roles'] });
    },
  });
}

export function useSetProductAccessActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      userId,
      isActive,
    }: {
      productId: string;
      userId: string;
      isActive: boolean;
    }) => productsApi.setAccessActive(productId, userId, isActive),
    onSuccess: (_, { productId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'access'] });
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'products'] });
      queryClient.invalidateQueries({ queryKey: ['access-matrix'] });
    },
  });
}

export function useUpdateProductAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      userId,
      data,
    }: {
      productId: string;
      userId: string;
      data: { roles?: string[]; permissions?: string[]; expires_at?: string | null };
    }) => productsApi.updateAccess(productId, userId, data),
    onSuccess: (_, { productId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'access'] });
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'products'] });
      queryClient.invalidateQueries({ queryKey: ['access-matrix'] });
    },
  });
}

export function useUpdateProductRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      currentName,
      data,
    }: {
      productId: string;
      currentName: string;
      data: { name?: string; description?: string; permissions?: string[] };
    }) => productsApi.updateRole(productId, currentName, data),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'roles'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'access'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
  });
}

export function useMyAccess() {
  return useQuery({
    queryKey: ['my-access'],
    queryFn: productsApi.myAccess,
  });
}

export function useMyAccessOverview() {
  return useQuery({
    queryKey: ['my-access', 'overview'],
    queryFn: productsApi.myAccessOverview,
  });
}
